import React, { useEffect, useState } from 'react'
import entete from "../../../assets/img/entete_pool.png"
import PropTypes from 'prop-types';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';

/**
 * Note à la victime — EXPERTISE MÉCANIQUE : invite le bénéficiaire à présenter
 * son véhicule endommagé au cabinet d'expertise (la NoteVictime standard, à
 * consonance médicale — « dossier médical, médecin expert » — ne convient pas).
 * Même gabarit lettre que NoteVictime, pleine page A4.
 */
const NoteVictimeMecanique = React.forwardRef((props, ref) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((error) => { console.log(error); });
        }
    }, []);

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const sinistre = props?.sinistre;
    const beneficiaire = props?.expertise?.beneficiaire?.personne;
    const expert = props?.expertise?.expert;
    const civilite = beneficiaire?.civilite || 'Monsieur';
    const nomExpert = `${expert?.nom || ''} ${expert?.prenom || ''}`.trim();
    // Véhicule expertisé = celui de la VICTIME / ADVERSAIRE de l'expertise (beneficiaire.engin),
    // pas l'engin assuré. Repli sur l'engin du sinistre / 1er engin du contrat si absent.
    const immatriculation = props?.expertise?.beneficiaire?.engin?.immatriculation
        || sinistre?.engin?.immatriculation
        || sinistre?.contrat?.engins?.[0]?.immatriculation
        || '……………';

    // En-tête + pied de page OPTIONNELS : désactivés → impression « nue » sur papier à en-tête,
    // avec corps CENTRÉ verticalement. Par défaut : affichés.
    const afficherEnteteEtPied = props?.afficherEnteteEtPied !== false;

    return (
        <div ref={ref} style={{
            fontFamily: "Calibri, 'Calibri Light', sans-serif",
            maxWidth: "800px",
            margin: "0 auto",
            padding: "20px 30px",
            lineHeight: 1.5,
            fontSize: "12pt",
            color: "#000000",
            display: "flex",
            flexDirection: "column",
            minHeight: "250mm",
            boxSizing: "border-box"
        }}>

            {/* En-tête avec logos — masquable pour impression sur papier à en-tête. */}
            {afficherEnteteEtPied && (
            <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                    {sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && logoPreviewUrl && (
                        <img src={logoPreviewUrl} style={{ width: "auto", height: "60px", objectFit: "contain" }} alt="Logo compagnie" />
                    )}
                    <img src={entete} style={{ width: "auto", height: "60px", objectFit: "contain" }} alt="En-tête" />
                </div>
                <div style={{ height: "2px", background: "linear-gradient(to right, #2563eb, #3b82f6)", marginTop: "8px" }} />
            </div>
            )}

            {/* Papier à en-tête : espace flexible en haut → centre le corps verticalement. */}
            {!afficherEnteteEtPied && <div style={{ flex: 1 }} />}

            {/* Référence en haut à gauche */}
            <div style={{ marginBottom: "35px" }}>
                <p style={{ margin: 0, fontSize: "11pt" }}>
                    <span style={{ fontWeight: "700" }}>Réf:</span> {sinistre?.numSinistre || '......'}/KGW/......./......./Pool TPVM-VT GIE
                </p>
            </div>

            {/* Destinataire à droite */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "35px" }}>
                <div style={{ textAlign: "left" }}>
                    <p style={{ margin: "0 0 3px 0", fontSize: "12pt", fontWeight: "700" }}>
                        {civilite} {beneficiaire?.prenom || ''} {beneficiaire?.nom || 'Bénéficiaire'}
                    </p>
                    <p style={{ margin: "0 0 3px 0", fontSize: "12pt" }}>
                        <span style={{ fontWeight: "700" }}>Tél:</span> {beneficiaire?.contact?.[0] || ''}
                    </p>
                    <p style={{ margin: "0 0 3px 0", fontSize: "12pt" }}>
                        Lomé – TOGO
                    </p>
                    <p style={{ margin: "8px 0 0 0", fontSize: "12pt" }}>
                        Lomé, le {today}
                    </p>
                </div>
            </div>

            {/* Objet */}
            <div style={{ marginBottom: "25px" }}>
                <p style={{ margin: 0, fontSize: "12pt" }}>
                    <span style={{ fontSize: "11pt", textDecoration: "underline", fontWeight: "700" }}>Objet:</span>{' '}
                    <span style={{ fontSize: "11pt", fontWeight: "700" }}>
                        SIN N° {sinistre?.numSinistre || ''} {sinistre?.contrat?.souscripteur?.nom || ''} C/ {beneficiaire?.nom || ''}
                    </span>
                </p>
                <p style={{ margin: "5px 0 0 60px", fontSize: "12pt" }}>
                    <span style={{ fontSize: "11pt", fontWeight: "700" }}>
                        Accident du {sinistre?.dateSinistre ? new Date(sinistre.dateSinistre).toLocaleDateString('fr-FR') : ''}
                    </span>
                </p>
            </div>

            {/* Corps du texte */}
            <div style={{ textAlign: "justify", fontSize: "12pt", lineHeight: 1.5 }}>
                <p style={{ marginBottom: "15px" }}>
                    {civilite},
                </p>

                <p style={{ marginBottom: "15px" }}>
                    Nous nous référons à l'affaire visée en marge et vous informons que nous sommes disposés à régler cette affaire à l'amiable.
                </p>

                <p style={{ marginBottom: "15px" }}>
                    À cet effet, nous vous prions de bien vouloir présenter votre véhicule
                    immatriculé <span style={{ fontWeight: "700" }}>{immatriculation}</span>, endommagé au cours de l'accident, au cabinet
                    d'expertise ci-après désigné, muni des pièces du véhicule (carte grise, visite technique…)
                    ainsi que de votre pièce d'identité, pour une expertise mécanique à l'adresse suivante :
                </p>

                {/* Bloc d'adresse de l'expert centré et en gras */}
                <div style={{ textAlign: "center", margin: "20px 0", fontSize: "12pt", fontWeight: "700" }}>
                    <p style={{ margin: "0 0 3px 0" }}>
                        Cabinet d'expertise {nomExpert || '……………'}
                    </p>
                    {expert?.adresse && (
                        <p style={{ margin: "0 0 3px 0" }}>
                            {expert.adresse}
                        </p>
                    )}
                    <p style={{ margin: "0 0 3px 0" }}>
                        Tél : {expert?.contact?.[0] || ''}
                    </p>
                    <p style={{ margin: "0 0 3px 0" }}>
                        Lomé – Togo
                    </p>
                </div>

                <p style={{ marginBottom: "15px" }}>
                    Nous vous invitons à prendre un rendez-vous avec l'expert avant de vous y rendre.
                </p>

                <p style={{ marginBottom: "15px" }}>
                    Par ailleurs, nous vous informons que vous pouvez, au cours de l'expertise, vous faire assister, à vos frais, d'un expert de votre choix.
                </p>

                <p style={{ marginBottom: "15px" }}>
                    Dès réception du rapport d'expertise, nous ne manquerons pas de revenir vers vous.
                </p>

                <p style={{ marginBottom: "15px" }}>
                    Veuillez agréer <span style={{ fontWeight: "700" }}>{civilite}</span>, nos salutations distinguées.
                </p>
            </div>

            {/* Signature */}
            <div style={{ marginTop: "50px", textAlign: "center" }}>
                <p style={{ margin: "0", fontSize: "11pt", fontWeight: "700", textDecoration: "underline" }}>
                    {props?.signataire?.nom || sinistre?.contrat?.organisme?.responsable || ''}
                </p>
                <p style={{ margin: "3px 0 0 0", fontSize: "11pt" }}>
                    {props?.signataire?.fonction || 'Directeur'}
                </p>
            </div>

            {/* Espace flexible : pousse le pied de page en bas de la feuille A4. */}
            <div style={{ flex: 1 }} />

            {afficherEnteteEtPied && <DocumentFooter />}
        </div>
    )
})

NoteVictimeMecanique.propTypes = {
    expertise: PropTypes.object,
    sinistre: PropTypes.object,
    signataire: PropTypes.object,
    /** false → imprimé sans en-tête ni pied (papier à en-tête), corps centré. */
    afficherEnteteEtPied: PropTypes.bool
}

export default NoteVictimeMecanique
